import { z } from 'zod';
import { create } from 'zustand';

import {
  vhAdmCreateAudience,
  vhAdmDeleteAudience,
  vhAdmGetAllPeople,
  vhAdmGetAudiences,
  vhAdmAssignSystemRole,
  vhAdmGetAuthUser,
  vhAdmGetAuthUsers,
  vhAdmDeleteServiceProvider,
  vhAdmDeleteVessel,
  vhAdmGetBetaFeatures,
  vhAdmGetServiceProvidersPage,
  vhAdmGetPeopleByIds,
  vhAdmGetServicePlans,
  vhAdmGetServiceProviders,
  vhAdmGetVessels,
  vhAdmGrantSubscription,
  vhAdmRevokeSubscription,
  vhAdmMarkBetaFeature,
  vhAdmSetPersonAudience,
  vhAdmUnmarkBetaFeature,
  vhAdmUpdateAudience,
  vhAdmCreateWhitelistEntry,
  vhAdmDeleteWhitelistEntry,
  vhAdmGetWhitelist,
  vhAdmGetWhitelistAudit,
  vhAdmGetWhitelistSettings,
  vhAdmSetWhitelistEnabled,
  vhAdmUpdateWhitelistEntry,
} from '@/admin/admin.api';
import { withFrontendFeatures } from '@/admin/admin.features';
import type {
  VhAdminAudience,
  VhAdminAudiencePayload,
  VhAdminFeature,
  VhAdminGrantSubscriptionPayload,
  VhAdminServicePlan,
  VhAdminServiceProvider,
  VhAdminSystemRole,
  VhAdminUser,
  VhAdminVessel,
  VhAdminWhitelistAuditRecord,
  VhAdminWhitelistEntry,
  VhAdminWhitelistPayload,
  VhAdminWhitelistSettings,
} from '@/admin/admin.types';
import { getPeopleByIds } from '@/store/persons/person.api';
import { SubscriptionTier } from '@/store/subscriptions/subscriptions.types';

const PAGE_SIZE = 20;

/** The most ids GET /subscriptions/services and service-provider/admin/info answer for in one call. */
const IDS_BATCH_SIZE = 100;

const inBatches = async <T>(ids: string[], read: (batch: string[]) => Promise<T[]>) => {
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += IDS_BATCH_SIZE) {
    batches.push(ids.slice(i, i + IDS_BATCH_SIZE));
  }
  return (await Promise.all(batches.map(read))).flat();
};

/**
 * The "User" default audience — seeded with a fixed id and the column default for
 * people.beta_group_id, so deleting an audience drops everyone on it back here.
 * See people migration 1788866266_add_beta_feature_groups.sql.
 */
/** The platform's own account (shared dto.SystemUserID): records it creates weren't made by a user. */
export const VH_ADMIN_SYSTEM_USER_ID = '019a0000-0000-7000-8000-000000000100';

export const VH_ADMIN_DEFAULT_AUDIENCE_ID = '019a0000-0000-7000-8000-000000000001';

/** Names the given people, leaving the ids in place if the lookup fails. */
const resolveNames = async (
  ids: (string | null)[],
  known: Record<string, string>,
): Promise<Record<string, string> | null> => {
  const missing = [...new Set(ids.filter((id): id is string => !!id && !known[id]))];
  if (missing.length === 0) return null;

  try {
    const people = await getPeopleByIds(missing);
    const resolved = { ...known };
    for (const person of people) {
      const { first_name, last_name } = person.person_info;
      resolved[person.id] = `${first_name ?? ''} ${last_name ?? ''}`.trim() || person.id;
    }
    return resolved;
  } catch {
    // A missing name is worth less than a broken screen.
    return null;
  }
};

/** Merges the vessels' live plans into `known`; a vessel left out of the answer is on Harbor. */
const resolvePlans = async (
  serviceIds: string[],
  known: Record<string, VhAdminServicePlan | null>,
): Promise<Record<string, VhAdminServicePlan | null>> => {
  if (serviceIds.length === 0) return known;

  try {
    const plans = await inBatches(serviceIds, vhAdmGetServicePlans);
    const resolved = { ...known };
    for (const id of serviceIds) resolved[id] = null;
    for (const plan of plans) resolved[plan.service_id] = plan;
    return resolved;
  } catch (err) {
    // Without it every vessel offers a grant and shows no seats, which is still a working screen.
    console.error("Failed to read the vessels' plans:", err);
    return known;
  }
};

type VhAdminState = {
  users: VhAdminUser[];
  usersCount: number;
  usersPage: number;
  usersSearch: string;
  usersRole: string;
  usersAudience: string;
  usersLoading: boolean;
  fetchUsers: (opts?: {
    page?: number;
    search?: string;
    role?: string;
    audience?: string;
  }) => Promise<void>;
  setUserAudience: (personId: string, audienceId: string) => Promise<void>;
  /** One person by id, for a link to someone who may not be on the loaded page. */
  fetchUserById: (id: string) => Promise<VhAdminUser | null>;
  /** Person id -> AUTH system role (user / admin / super-admin), read when their panel opens. */
  userSystemRoles: Record<string, string>;
  fetchUserSystemRole: (id: string) => Promise<void>;
  setUserSystemRole: (id: string, role: VhAdminSystemRole) => Promise<void>;

  features: VhAdminFeature[];
  featuresLoading: boolean;
  fetchFeatures: () => Promise<void>;
  setFeatureBeta: (name: string, beta: boolean) => Promise<void>;

  audiences: VhAdminAudience[];
  audiencesLoading: boolean;
  /** Person id -> display name, filled in for whoever created each audience. */
  audienceCreators: Record<string, string>;
  fetchAudiences: () => Promise<void>;
  createAudience: (payload: VhAdminAudiencePayload) => Promise<void>;
  updateAudience: (id: string, payload: VhAdminAudiencePayload) => Promise<void>;
  deleteAudience: (id: string) => Promise<void>;
  setAudienceTargetsFeature: (id: string, feature: string, targeted: boolean) => Promise<void>;

  vessels: VhAdminVessel[];
  vesselsCount: number;
  vesselsPage: number;
  vesselsSearch: string;
  vesselsLoading: boolean;
  /** Person id -> display name for each vessel's owner, or its creator when it has none. */
  vesselOwners: Record<string, string>;
  /** Vessel id -> its live plan, or null when it has none and so is on Harbor. */
  vesselPlans: Record<string, VhAdminServicePlan | null>;
  fetchVessels: (opts?: { page?: number; search?: string }) => Promise<void>;
  grantSubscription: (payload: VhAdminGrantSubscriptionPayload) => Promise<void>;
  revokeSubscription: (serviceId: string) => Promise<void>;

  /** The vessels and providers of the person open in the users panel; one deleted since is missing. */
  userVessels: VhAdminVessel[];
  userProviders: VhAdminServiceProvider[];
  userServicesLoading: boolean;
  fetchUserServices: (user: VhAdminUser) => Promise<void>;
  /** Super admins only. Removes the vessel and refreshes the page it was on. */
  deleteVessel: (id: string) => Promise<void>;

  serviceProviders: VhAdminServiceProvider[];
  serviceProvidersCount: number;
  serviceProvidersPage: number;
  serviceProvidersSearch: string;
  serviceProvidersLoading: boolean;
  /** Person id -> display name for whoever created each provider. */
  serviceProviderCreators: Record<string, string>;
  fetchServiceProviders: (opts?: { page?: number; search?: string }) => Promise<void>;
  /** Super admins only. Removes the provider and refreshes the page it was on. */
  deleteServiceProvider: (id: string) => Promise<void>;

  whitelist: VhAdminWhitelistEntry[];
  whitelistCount: number;
  whitelistPage: number;
  whitelistSearch: string;
  whitelistLoading: boolean;
  /** Null until the first read: the switch shows nothing rather than a guess. */
  whitelistSettings: VhAdminWhitelistSettings | null;
  whitelistAudit: VhAdminWhitelistAuditRecord[];
  whitelistAuditCount: number;
  whitelistAuditPage: number;
  whitelistAuditLoading: boolean;
  /**
   * Person id -> display name for whoever added an entry, changed the switch or made a change, and
   * for each linked account. A person with no name maps to their own id.
   */
  whitelistPeople: Record<string, string>;
  fetchWhitelist: (opts?: { page?: number; search?: string }) => Promise<void>;
  fetchWhitelistSettings: () => Promise<void>;
  fetchWhitelistAudit: (page?: number) => Promise<void>;
  createWhitelistEntry: (payload: VhAdminWhitelistPayload) => Promise<void>;
  setWhitelistEnabled: (enabled: boolean) => Promise<void>;
  updateWhitelistEntry: (id: string, payload: VhAdminWhitelistPayload) => Promise<void>;
  /** Every entry linked to one account: an email and a phone can both sign in to it. */
  fetchAccountWhitelist: (userId: string) => Promise<VhAdminWhitelistEntry[]>;
  deleteWhitelistEntry: (id: string, account?: boolean) => Promise<void>;
};

/**
 * A membership's `service_type`, from the people.services enum. Hyphenated there, unlike the
 * `service_provider` of the newer service_registry table.
 */
export const VH_ADMIN_SERVICE_TYPE = {
  VESSEL: 'vessel',
  SERVICE_PROVIDER: 'service-provider',
} as const;

/** The person's memberships of one kind of service; their own personal space is neither kind. */
export const vhAdminUserServices = (user: VhAdminUser, type: string) =>
  (user.services ?? []).filter((s) => s.service_type === type);

/** Which of the user panel's requests is the latest, so a slow answer for another person is dropped. */
let userServicesRequest = 0;

/** Who holds a vessel's plan: its owner, or the person who created it while it has none. */
export const vhAdminVesselHolder = (vessel: VhAdminVessel) => vessel.owner_id ?? vessel.created_by;

export const useVhAdminStore = create<VhAdminState>((set, get) => ({
  users: [],
  usersCount: 0,
  usersPage: 1,
  usersSearch: '',
  usersRole: '',
  usersAudience: '',
  usersLoading: false,
  fetchUsers: async (opts) => {
    const page = opts?.page ?? get().usersPage;
    const search = opts?.search ?? get().usersSearch;
    const role = opts?.role ?? get().usersRole;
    const audience = opts?.audience ?? get().usersAudience;

    set({
      usersLoading: true,
      usersPage: page,
      usersSearch: search,
      usersRole: role,
      usersAudience: audience,
    });
    try {
      const data = await vhAdmGetAllPeople({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        role: role || undefined,
        beta_group_id: audience || undefined,
      });
      set({ users: data.items, usersCount: data.count });

      // The table's system role column. Secondary: a failure leaves it empty, not the page.
      if (data.items.length > 0) {
        try {
          const ids = data.items.map((user) => user.id);
          const accounts = await vhAdmGetAuthUsers(ids);
          const roles = { ...get().userSystemRoles };
          for (const account of accounts) {
            if (account.role) roles[account.id] = account.role;
          }

          // An auth older than the role on /user/find answers without one: read those one by one.
          const missing = ids.filter((id) => !roles[id]);
          const fallback = await Promise.allSettled(missing.map((id) => vhAdmGetAuthUser(id)));
          for (const result of fallback) {
            if (result.status === 'fulfilled' && result.value.role) {
              roles[result.value.id] = result.value.role;
            }
          }

          set({ userSystemRoles: roles });
        } catch (error) {
          console.error('Failed to read the system roles of the users page:', error);
        }
      }
    } finally {
      set({ usersLoading: false });
    }
  },
  setUserAudience: async (personId, audienceId) => {
    const previous = get().users;
    set({
      users: previous.map((user) =>
        user.id === personId ? { ...user, beta_group_id: audienceId } : user,
      ),
    });
    try {
      await vhAdmSetPersonAudience(personId, audienceId);
    } catch (error) {
      set({ users: previous });
      throw error;
    }
  },

  fetchUserById: async (id) => {
    const [user] = await vhAdmGetPeopleByIds([id]);
    return user ?? null;
  },
  userSystemRoles: {},
  fetchUserSystemRole: async (id) => {
    const user = await vhAdmGetAuthUser(id);
    set({ userSystemRoles: { ...get().userSystemRoles, [id]: user.role } });
  },
  setUserSystemRole: async (id, role) => {
    await vhAdmAssignSystemRole(id, role);
    set({ userSystemRoles: { ...get().userSystemRoles, [id]: role } });
  },

  features: [],
  featuresLoading: false,
  fetchFeatures: async () => {
    set({ featuresLoading: true });
    try {
      const features = await vhAdmGetBetaFeatures();
      set({ features: withFrontendFeatures(features) });
    } finally {
      set({ featuresLoading: false });
    }
  },
  setFeatureBeta: async (name, beta) => {
    const previous = get().features;
    const feature = previous.find((item) => item.name === name);
    if (!feature) return;

    set({
      features: previous.map((item) => (item.name === name ? { ...item, beta } : item)),
    });
    try {
      if (beta) {
        await vhAdmMarkBetaFeature(name, feature.type);
      } else {
        await vhAdmUnmarkBetaFeature(name);
      }
    } catch (error) {
      set({ features: previous });
      throw error;
    }
    // "All features" stands for whatever is beta right now, so its list just moved — and an
    // unmarked frontend feature has left every audience that held it.
    if (get().audiences.length > 0) await get().fetchAudiences();
  },

  audiences: [],
  audiencesLoading: false,
  audienceCreators: {},
  fetchAudiences: async () => {
    set({ audiencesLoading: true });
    try {
      const audiences = await vhAdmGetAudiences();
      set({ audiences });

      const creators = await resolveNames(
        audiences.map((audience) => audience.created_by),
        get().audienceCreators,
      );
      if (creators) set({ audienceCreators: creators });
    } finally {
      set({ audiencesLoading: false });
    }
  },
  createAudience: async (payload) => {
    const created = await vhAdmCreateAudience(payload);
    set({ audiences: [...get().audiences, created] });

    const creators = await resolveNames([created.created_by], get().audienceCreators);
    if (creators) set({ audienceCreators: creators });
  },
  updateAudience: async (id, payload) => {
    const previous = get().audiences;
    set({
      audiences: previous.map((audience) =>
        audience.id === id ? { ...audience, ...payload } : audience,
      ),
    });
    try {
      await vhAdmUpdateAudience(id, payload);
    } catch (error) {
      set({ audiences: previous });
      throw error;
    }
  },
  deleteAudience: async (id) => {
    const previousAudiences = get().audiences;
    const previousUsers = get().users;
    set({
      audiences: previousAudiences.filter((audience) => audience.id !== id),
      // The backend returns everyone on a deleted audience to the "User" default.
      users: previousUsers.map((user) =>
        user.beta_group_id === id ? { ...user, beta_group_id: VH_ADMIN_DEFAULT_AUDIENCE_ID } : user,
      ),
    });
    try {
      await vhAdmDeleteAudience(id);
    } catch (error) {
      set({ audiences: previousAudiences, users: previousUsers });
      throw error;
    }
  },
  setAudienceTargetsFeature: async (id, feature, targeted) => {
    const audience = get().audiences.find((item) => item.id === id);
    if (!audience) return;

    const features = targeted
      ? [...audience.features, feature].sort()
      : audience.features.filter((name) => name !== feature);

    await get().updateAudience(id, { name: audience.name, features });
  },

  vessels: [],
  vesselsCount: 0,
  vesselsPage: 1,
  vesselsSearch: '',
  vesselsLoading: false,
  vesselOwners: {},
  vesselPlans: {},
  fetchVessels: async (opts) => {
    const page = opts?.page ?? get().vesselsPage;
    const search = opts?.search ?? get().vesselsSearch;

    set({ vesselsLoading: true, vesselsPage: page, vesselsSearch: search });
    try {
      const data = await vhAdmGetVessels({ page, limit: PAGE_SIZE, search: search || undefined });
      // Read before the rows show, so a granted vessel never flashes a grant button.
      const plans = await resolvePlans(
        data.items.map((vessel) => vessel.id),
        get().vesselPlans,
      );
      set({ vessels: data.items, vesselsCount: data.count, vesselPlans: plans });

      const owners = await resolveNames(data.items.map(vhAdminVesselHolder), get().vesselOwners);
      if (owners) set({ vesselOwners: owners });
    } finally {
      set({ vesselsLoading: false });
    }
  },
  grantSubscription: async (payload) => {
    await vhAdmGrantSubscription(payload);
    set({
      vessels: get().vessels.map((vessel) =>
        vessel.id === payload.service_id ? { ...vessel, subscription_tier: payload.tier } : vessel,
      ),
      vesselPlans: {
        ...get().vesselPlans,
        [payload.service_id]: {
          service_id: payload.service_id,
          tier: payload.tier,
          seats: payload.seats,
          crew_seats: payload.crew_seats,
          payment_status: get().vesselPlans[payload.service_id]?.payment_status ?? 'active',
          manually_granted: true,
        },
      },
    });
  },
  revokeSubscription: async (serviceId) => {
    await vhAdmRevokeSubscription(serviceId);
    set({
      vessels: get().vessels.map((vessel) =>
        vessel.id === serviceId
          ? { ...vessel, subscription_tier: SubscriptionTier.HARBOR }
          : vessel,
      ),
    });
    // The Harbor plan it falls back to is the server's to size, so read it rather than guess.
    set({ vesselPlans: await resolvePlans([serviceId], get().vesselPlans) });
  },

  userVessels: [],
  userProviders: [],
  userServicesLoading: false,
  fetchUserServices: async (user) => {
    const request = ++userServicesRequest;
    const ids = (type: string) => vhAdminUserServices(user, type).map((s) => s.service_id);
    const vesselIds = ids(VH_ADMIN_SERVICE_TYPE.VESSEL);
    const providerIds = ids(VH_ADMIN_SERVICE_TYPE.SERVICE_PROVIDER);

    set({
      userVessels: [],
      userProviders: [],
      userServicesLoading: vesselIds.length + providerIds.length > 0,
    });
    if (vesselIds.length + providerIds.length === 0) return;

    try {
      const [vessels, plans, providers] = await Promise.all([
        vesselIds.length > 0
          ? vhAdmGetVessels({ page: 1, limit: vesselIds.length, ids: vesselIds }).then(
              (data) => data.items,
            )
          : [],
        resolvePlans(vesselIds, get().vesselPlans),
        inBatches(providerIds, vhAdmGetServiceProviders),
      ]);
      if (request !== userServicesRequest) return;

      set({ userVessels: vessels, userProviders: providers, vesselPlans: plans });
    } finally {
      if (request === userServicesRequest) set({ userServicesLoading: false });
    }
  },

  deleteVessel: async (id) => {
    await vhAdmDeleteVessel(id);
    await get().fetchVessels();
    // The last vessel of the last page leaves it empty: step back to the one before.
    const { vessels, vesselsPage } = get();
    if (vessels.length === 0 && vesselsPage > 1)
      await get().fetchVessels({ page: vesselsPage - 1 });
  },

  serviceProviders: [],
  serviceProvidersCount: 0,
  serviceProvidersPage: 1,
  serviceProvidersSearch: '',
  serviceProvidersLoading: false,
  serviceProviderCreators: {},
  fetchServiceProviders: async (opts) => {
    const page = opts?.page ?? get().serviceProvidersPage;
    const search = opts?.search ?? get().serviceProvidersSearch;

    set({
      serviceProvidersLoading: true,
      serviceProvidersPage: page,
      serviceProvidersSearch: search,
    });
    try {
      // A whole address goes to the exact email filter; anything else, a half-typed one
      // included, searches names and places.
      const query = search.trim();
      const isEmail = z.string().email().safeParse(query).success;
      const data = await vhAdmGetServiceProvidersPage({
        page,
        limit: PAGE_SIZE,
        search: !isEmail && query ? query : undefined,
        email: isEmail ? query : undefined,
      });
      set({ serviceProviders: data.items, serviceProvidersCount: data.count });

      const creators = await resolveNames(
        data.items.map((provider) => provider.creator_id),
        get().serviceProviderCreators,
      );
      if (creators) set({ serviceProviderCreators: creators });
    } finally {
      set({ serviceProvidersLoading: false });
    }
  },
  deleteServiceProvider: async (id) => {
    await vhAdmDeleteServiceProvider(id);
    await get().fetchServiceProviders();
    const { serviceProviders, serviceProvidersPage } = get();
    if (serviceProviders.length === 0 && serviceProvidersPage > 1) {
      await get().fetchServiceProviders({ page: serviceProvidersPage - 1 });
    }
  },

  whitelist: [],
  whitelistCount: 0,
  whitelistPage: 1,
  whitelistSearch: '',
  whitelistLoading: false,
  whitelistSettings: null,
  whitelistAudit: [],
  whitelistAuditCount: 0,
  whitelistAuditPage: 1,
  whitelistAuditLoading: false,
  whitelistPeople: {},
  fetchWhitelist: async (opts) => {
    const page = opts?.page ?? get().whitelistPage;
    const search = opts?.search ?? get().whitelistSearch;

    set({ whitelistLoading: true, whitelistPage: page, whitelistSearch: search });
    try {
      const data = await vhAdmGetWhitelist({ page, limit: PAGE_SIZE, search: search || undefined });
      set({ whitelist: data.items, whitelistCount: data.count });

      // Who added each entry, and the person behind a linked account (person id = user id),
      // whose name stands in when the entry has none of its own.
      const people = await resolveNames(
        data.items.flatMap((entry) => [entry.created_by, entry.user_id]),
        get().whitelistPeople,
      );
      if (people) set({ whitelistPeople: people });
    } finally {
      set({ whitelistLoading: false });
    }
  },
  fetchWhitelistSettings: async () => {
    const settings = await vhAdmGetWhitelistSettings();
    set({ whitelistSettings: settings });

    const people = await resolveNames([settings.updated_by], get().whitelistPeople);
    if (people) set({ whitelistPeople: people });
  },
  fetchWhitelistAudit: async (page = get().whitelistAuditPage) => {
    set({ whitelistAuditLoading: true, whitelistAuditPage: page });
    try {
      const data = await vhAdmGetWhitelistAudit({ page, limit: PAGE_SIZE });
      set({ whitelistAudit: data.items, whitelistAuditCount: data.count });

      const people = await resolveNames(
        data.items.map((record) => record.actor_id),
        get().whitelistPeople,
      );
      if (people) set({ whitelistPeople: people });
    } finally {
      set({ whitelistAuditLoading: false });
    }
  },
  createWhitelistEntry: async (payload) => {
    await vhAdmCreateWhitelistEntry(payload);
    // The new entry sorts first, so go back to the page that shows it.
    await Promise.all([get().fetchWhitelist({ page: 1 }), get().fetchWhitelistAudit(1)]);
  },
  setWhitelistEnabled: async (enabled) => {
    const settings = await vhAdmSetWhitelistEnabled(enabled);
    set({ whitelistSettings: settings });

    const people = await resolveNames([settings.updated_by], get().whitelistPeople);
    if (people) set({ whitelistPeople: people });
    await get().fetchWhitelistAudit(1);
  },
  updateWhitelistEntry: async (id, payload) => {
    const updated = await vhAdmUpdateWhitelistEntry(id, payload);
    set({ whitelist: get().whitelist.map((entry) => (entry.id === id ? updated : entry)) });

    // A new contact may link another account, whose name the list hasn't read yet.
    const people = await resolveNames([updated.user_id], get().whitelistPeople);
    if (people) set({ whitelistPeople: people });
    await get().fetchWhitelistAudit(1);
  },
  fetchAccountWhitelist: async (userId) => {
    const data = await vhAdmGetWhitelist({ page: 1, limit: PAGE_SIZE, user_id: userId });
    return data.items;
  },
  deleteWhitelistEntry: async (id, account = false) => {
    await vhAdmDeleteWhitelistEntry(id, account);
    await Promise.all([get().fetchWhitelist(), get().fetchWhitelistAudit(1)]);
    // Removing the last entry of the last page leaves it empty: step back to the one before.
    const { whitelist, whitelistPage } = get();
    if (whitelist.length === 0 && whitelistPage > 1) {
      await get().fetchWhitelist({ page: whitelistPage - 1 });
    }
  },
}));

export const VH_ADMIN_PAGE_SIZE = PAGE_SIZE;
